import fixWebmDuration from 'fix-webm-duration'
import {makeAutoObservable, runInAction} from 'mobx'
import {Lang} from 'yougile-lang'
import {ensure, logger, Time, Util} from 'yougile-util'
import {Hub} from 'yougile-model'
import {Backend} from '@common/backend'
import {Tracking, TrackingEvent} from '@common/tracking'
import {VideoPreview} from '@common/video-preview'
import {showConfirm} from '@components/common/base/notifications'
import {FileUploadProgress} from '@components/file-upload-progress'
import {MessageBox} from '@components/message-box'
import {Recorder} from './recorder'

export interface ScreencastHandlers {
  onDoneUpload: (url: string, hub: Hub) => void
  onUploadFailed: (file: File) => void
}

interface State {
  recording: boolean
  controlsVisible: boolean
  countdown: number
  previewUrl: string
  previewFile: File | undefined
  handlers: ScreencastHandlers | undefined
  recordingStartTime: number | undefined
  elapsedTime: string
  elapsedUpdateTimer: TimeoutHandler | undefined
  pauseStartTime: number | undefined
  withSound: boolean
}

export class ScreencastStore {
  state: State = {
    recording: false,
    controlsVisible: false,
    countdown: 0,
    previewUrl: '',
    previewFile: undefined,
    handlers: undefined,
    recordingStartTime: undefined,
    elapsedTime: '',
    elapsedUpdateTimer: undefined,
    pauseStartTime: undefined,
    withSound: true
  }

  hub?: Hub

  constructor() {
    makeAutoObservable(this, undefined, {autoBind: true})
  }

  async onRecordingFinished(file: File): Promise<void> {
    this.state.previewFile = file
    const url = URL.createObjectURL(file)
    this.state.previewUrl = url
  }

  private removePreview(): void {
    URL.revokeObjectURL(this.state.previewUrl)
    this.state.previewUrl = ''
    this.state.previewFile = undefined
  }

  cancelSend(): void {
    void Tracking.send(TrackingEvent.screencastCancel)
    this.removePreview()
  }

  async sendToChat(): Promise<void> {
    if (!this.state.previewFile) {
      return
    }
    const sizeMb = Math.ceil(
      (this.state.previewFile?.size ?? 0) / (1024 * 1024)
    )
    if (sizeMb > Backend.uploadFileLimit) {
      MessageBox.warn(
        Lang.msg.screencast.tooLargeFileToUpload(
          sizeMb,
          Backend.uploadFileLimit
        )
      )
      return
    }
    void Tracking.send(TrackingEvent.screencastSend)
    const file = this.state.previewFile
    this.removePreview()

    const previewParams: string[] = []
    let url: string | undefined
    try {
      url = await FileUploadProgress.saveFile(file, {
        beforeCompletion: async url => {
          const previewUrl = await VideoPreview.createPreview(url).catch(e =>
            logger.error(e)
          )
          if (previewUrl) {
            const uuid =
              previewUrl.split('/user-data/').pop()?.split('/')[0] ?? ''
            previewParams.push('preview=' + uuid)
          }
        }
      })
    } catch (e) {
      logger.error('Error while saving file', e)
      url = undefined
    }

    if (!url) {
      const shouldRetry = await showConfirm(
        Lang.msg.screencast.uploadFailedTitle,
        false,
        true,
        {
          primaryBtnText: Lang.msg.screencast.uploadFailedRetry,
          closeBtnText: Lang.msg.screencast.uploadFailedCancel,
          title: Lang.msg.screencast.uploadFailedTitle,
          subtitle: Lang.msg.screencast.uploadFailedMessage
        }
      )

      if (shouldRetry) {
        this.state.previewFile = file
        const url = URL.createObjectURL(file)
        this.state.previewUrl = url
        return this.sendToChat()
      } else {
        this.state.handlers?.onUploadFailed(file)
        return
      }
    }

    const urlWithPreview = `${url}?${previewParams.join('&')}`
    this.hub && this.state.handlers?.onDoneUpload(urlWithPreview, this.hub)
  }

  async startRecording(handlers: ScreencastHandlers, hub: Hub): Promise<void> {
    if (this.state.recording) {
      MessageBox.warn(Lang.msg.screencast.alreadyRecording)
      return
    }
    this.hub = hub
    this.state.recording = true
    this.state.handlers = handlers
    try {
      const file = await this.recordScreencast()
      await this.onRecordingFinished(file)
    } catch (e) {
      logger.error('Error while recording screencast', e)
    } finally {
      this.state.recording = false
    }
  }

  pause(): void {
    this.state.recording = false
    Recorder.pause()
    this.elapsedTimePause()
  }

  resume(): void {
    void Tracking.send(TrackingEvent.screencastPause)
    this.state.recording = true
    Recorder.resume()
    this.elapsedTimeResume()
  }

  stopRecording(): void {
    this.elapsedTimeResume()
    void Tracking.send(TrackingEvent.screencastResume)
    Recorder.stop()
  }

  elapsedTimePause(): void {
    this.state.pauseStartTime = Time.now()
  }

  elapsedTimeResume(): void {
    if (!this.state.pauseStartTime || !this.state.recordingStartTime) {
      return
    }
    const pausedFor = Time.now() - this.state.pauseStartTime
    this.state.recordingStartTime += pausedFor
    this.state.pauseStartTime = undefined
  }

  updateElapsedTime(): void {
    if (!this.state.recordingStartTime || !this.state.recording) {
      return
    }

    const sec = Math.floor((Time.now() - this.state.recordingStartTime) / 1000)
    const mm = Math.floor(sec / 60)
    const ss = sec % 60

    const zz = (n: number): string => ('00' + n).slice(-2)
    this.state.elapsedTime = [mm, ss].map(zz).join(':')
  }

  showControls(): void {
    this.state.controlsVisible = true
    this.state.recordingStartTime = Time.now()
    this.updateElapsedTime()
    this.state.elapsedUpdateTimer = setInterval(() => {
      this.updateElapsedTime()
    }, 500)

    // this hack is because of angular-react interop difficulties
    // TODO: remove this after angular migration finished
    document
      .querySelector('.loggedin-below')
      ?.classList.add('loggedin-below--has-bottom-controls')
  }

  hideControls(): void {
    this.state.controlsVisible = false
    this.state.recordingStartTime = undefined
    this.state.elapsedTime = ''
    if (this.state.elapsedUpdateTimer) {
      clearInterval(this.state.elapsedUpdateTimer)
    }

    // this hack is because of angular-react interop difficulties
    // TODO: remove this after angular migration finished
    document
      .querySelector('.loggedin-below')
      ?.classList.remove('loggedin-below--has-bottom-controls')
  }

  async makeCountdown(): Promise<void> {
    for (let i = 3; i >= 1; i--) {
      runInAction(() => {
        this.state.countdown = i
      })
      await Util.wait(1000)
    }
    runInAction(() => {
      this.state.countdown = 0
    })
  }

  async recordScreencast(): Promise<File> {
    void Tracking.send(TrackingEvent.screencastClick)
    const {withSound} = await Recorder.requestPermissions()
    this.state.withSound = withSound

    await this.makeCountdown()
    this.showControls()
    void Tracking.send(TrackingEvent.screencastStart)

    const blob = await Recorder.start()
    ensure(blob, 'Failed to record screen')
    const fixedDuration = await this.fixDuration(blob)
    const file = new File(
      [fixedDuration],
      `Screencast-${Time.formatDateTime(Time.now()).replace(/[:]/g, '-')}.mp4`
    )

    await Util.wait(100)
    this.hideControls()
    void Tracking.send(TrackingEvent.screencastRecordingFinished)

    return file
  }

  private async fixDuration(blob: Blob): Promise<Blob> {
    if (!this.state.recordingStartTime) {
      return blob
    }
    const duration = Time.now() - this.state.recordingStartTime

    try {
      const result = await fixWebmDuration(blob, duration)
      return result
    } catch (e) {
      logger.error('Failed to fix duration', e)
      return blob
    }
  }
}

export const screencastStore = new ScreencastStore()
